import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { useQuery } from '@tanstack/react-query';
import type { Membership, MiniLeague, User } from '@h2h/shared';
import { db } from '../../lib/firebase';
import { LIVE_POLL } from '../firestore/live';

// ── Mini (private) leagues ─────────────────────────────────────────────────────

/** Private leagues the user belongs to, optionally scoped to a base league. */
export function useMiniLeagues(uid: string | null, leagueId?: string) {
  return useQuery({
    queryKey: ['miniLeagues', uid, leagueId ?? null],
    enabled: !!uid,
    queryFn: async (): Promise<MiniLeague[]> => {
      const q = query(collection(db, 'miniLeagues'), where('memberIds', 'array-contains', uid));
      const snap = await getDocs(q);
      let leagues = snap.docs.map((d) => d.data() as MiniLeague);
      if (leagueId) leagues = leagues.filter((l) => l.leagueId === leagueId);
      return leagues.sort((a, b) => a.createdAt - b.createdAt);
    }
  });
}

/** A single private league (members-only read). */
export function useMiniLeague(miniLeagueId: string | undefined) {
  return useQuery({
    queryKey: ['miniLeague', miniLeagueId],
    enabled: !!miniLeagueId,
    queryFn: async (): Promise<MiniLeague | null> => {
      const snap = await getDoc(doc(db, 'miniLeagues', miniLeagueId!));
      return snap.exists() ? (snap.data() as MiniLeague) : null;
    }
  });
}

/** One row of a mini-league standings table. */
export interface MiniLeagueStandingRow {
  userId: string;
  displayName: string;
  avatarTone: number;
  tier: number;
  /** Lifetime vanity points on the base league (the ranking key). */
  seasonPoints: number;
  /** Points scored in the most recently settled round (the ROUND column). */
  roundDelta: number;
  /** 1-based rank (after sorting by seasonPoints). */
  rank: number;
  /** Positions gained (+) or lost (−) vs the previous round, or 0 if unchanged. */
  rankChange: number;
  joinedRoundId?: string | null;
  isYou: boolean;
}

/**
 * Standings for a mini league, ranked by each member's vanity `seasonPoints` on
 * the base league. The per-round delta and rank-change arrows come from the vanity
 * `lastRoundScore` — re-ranking on `seasonPoints − lastRoundScore` reconstructs the
 * previous round's order, so we never read another member's private entry.
 */
export function useMiniLeagueStandings(uid: string | null, mini: MiniLeague | null | undefined) {
  const memberIds = mini?.memberIds ?? [];
  const leagueId = mini?.leagueId;
  return useQuery({
    queryKey: ['miniLeagueStandings', mini?.miniLeagueId, [...memberIds].sort().join(',')],
    enabled: !!leagueId && memberIds.length > 0,
    refetchInterval: LIVE_POLL,
    queryFn: async (): Promise<MiniLeagueStandingRow[]> => {
      const raw = await Promise.all(
        memberIds.map(async (id) => {
          const [mSnap, uSnap] = await Promise.all([
            getDoc(doc(db, 'memberships', `${id}_${leagueId}`)),
            getDoc(doc(db, 'users', id))
          ]);
          if (!mSnap.exists()) return null;
          const m = mSnap.data() as Membership;
          const u = uSnap.exists() ? (uSnap.data() as User) : null;
          const seasonPoints = m.seasonPoints ?? 0;
          const roundDelta = m.lastRoundScore ?? 0;
          return {
            userId: id,
            displayName: u?.displayName ?? 'Player',
            avatarTone: u?.avatarTone ?? 0,
            tier: m.tier,
            seasonPoints,
            roundDelta,
            prevPoints: seasonPoints - roundDelta,
            joinedRoundId: mini?.members?.[id]?.joinedRoundId ?? null,
            isYou: id === uid
          };
        })
      );
      const members = raw.filter((r): r is NonNullable<typeof r> => r != null);

      // Current order by season points (the live table).
      const byNow = [...members].sort((a, b) => b.seasonPoints - a.seasonPoints || b.tier - a.tier);
      // Reconstructed previous order to derive rank movement.
      const prevRank = new Map<string, number>();
      [...members]
        .sort((a, b) => b.prevPoints - a.prevPoints || b.tier - a.tier)
        .forEach((r, i) => prevRank.set(r.userId, i + 1));

      return byNow.map((r, i) => {
        const rank = i + 1;
        const before = prevRank.get(r.userId) ?? rank;
        return {
          userId: r.userId,
          displayName: r.displayName,
          avatarTone: r.avatarTone,
          tier: r.tier,
          seasonPoints: r.seasonPoints,
          roundDelta: r.roundDelta,
          rank,
          rankChange: before - rank,
          joinedRoundId: r.joinedRoundId,
          isYou: r.isYou
        } as MiniLeagueStandingRow;
      });
    }
  });
}
